import { useCallback ,
         useEffect   ,
         useMemo     ,
         useRef      ,
         useState    } from 'react';

import { ChatModChannel      ,
         ChatModMessage      ,
         ChatModBan          ,
         ChatDecision        ,
         ChatSanction        ,
         ChatMessageStatus   ,
         ChatModerationLevel } from '../../../dataTypes/moderationManagment/chat_types';

import { fetchChatModChannels ,
         fetchChatModMessages ,
         decideChatMessages   ,
         sanctionChatAuthor   ,
         updateChatModChannel ,
         fetchChatBans        ,
         liftChatBan          ,
         ChatModerationError  ,
         ChatSanctionResult   } from '../../../backendServices/moderationManagment/api_chat';

import { demoApi              ,
         isChatModerationDemo } from './chatModerationDemo';

/**
 * État de l'onglet Chat en direct : chaînes suivies, file à valider, fil des
 * décisions, et les actions de la régie.
 *
 * Décisions OPTIMISTES : un message validé quitte la file à l'instant de
 * l'appui, sans attendre le serveur — en direct, un modérateur enchaîne les
 * messages au clavier, il ne peut pas attendre 300 ms à chaque fois. Le choix
 * local (`overrides`) l'emporte sur les relevés jusqu'à ce que le serveur le
 * confirme ; un échec le retire et l'écran le dit.
 */

const MESSAGES_EVERY_MS        = 3_000;
const MESSAGES_HIDDEN_EVERY_MS = 10_000;
const CHANNELS_EVERY_MS        = 10_000;
const OVERRIDE_TTL_MS          = 20_000;

const demo = isChatModerationDemo();

const api = demo ? {
  channels      : (_: number)                          => demoApi.channels(),
  messages      : (_: number, ids: number[])           => demoApi.messages(ids),
  decide        : (_: number, ids: number[], a: ChatDecision) => demoApi.decide(ids, a),
  sanction      : (_: number, id: number, a: ChatSanction)    => demoApi.sanction(id, a) as Promise<ChatSanctionResult>,
  updateChannel : (_: number, id: number, change: { chatOpen?: boolean; level?: ChatModerationLevel }) => demoApi.updateChannel(id, change),
  bans          : (_: number)                          => demoApi.bans(),
  unban         : (_: number, id: number)              => demoApi.unban(id),
} : {
  channels      : fetchChatModChannels,
  messages      : fetchChatModMessages,
  decide        : decideChatMessages,
  sanction      : sanctionChatAuthor,
  updateChannel : updateChatModChannel,
  bans          : fetchChatBans,
  unban         : liftChatBan,
};

const storageKey = (adminId: number) => `kleon.chatModeration.channels.${adminId}`;

const readSelection = (adminId: number): number[] | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(adminId));
    const ids = raw ? JSON.parse(raw) : null;
    return Array.isArray(ids) ? ids.filter((v: unknown) => typeof v === 'number') : null;
  } catch { return null; }
};

const writeSelection = (adminId: number, ids: number[]) => {
  try { window.localStorage.setItem(storageKey(adminId), JSON.stringify(ids)); } catch { /* préférence du poste, sans gravité */ }
};

const describeError = (error: unknown): string => {
  if (error instanceof ChatModerationError && error.status === 404)
    return "Le service de modération du chat ne répond pas (404) : il n'est pas encore déployé sur le serveur.";
  if (error instanceof ChatModerationError) return error.message;
  return "Le serveur ne répond pas. La file reprendra d'elle-même dès qu'il reviendra.";
};

interface Override { status: ChatMessageStatus; at: number; }

export interface ChatModerationState {
  isDemo          : boolean;
  channels        : ChatModChannel[];
  channelsLoaded  : boolean;
  selectedIds     : number[];
  pending         : ChatModMessage[];
  recent          : ChatModMessage[];
  error           : string;
  lastSync        : number | null;
  bans            : ChatModBan[];
  toggleChannel   : (id: number) => void;
  selectChannels  : (ids: number[]) => void;
  decide          : (ids: number[], action: ChatDecision) => Promise<boolean>;
  sanction        : (messageId: number, action: ChatSanction) => Promise<ChatSanctionResult | null>;
  updateChannel   : (id: number, change: { chatOpen?: boolean; level?: ChatModerationLevel }) => Promise<boolean>;
  loadBans        : () => Promise<void>;
  liftBan         : (banId: number) => Promise<boolean>;
  clearError      : () => void;
}

export const useChatModeration = (adminId: number | null): ChatModerationState => {

  const [ channels       , setChannels       ] = useState<ChatModChannel[]>([]);
  const [ channelsLoaded , setChannelsLoaded ] = useState(false);
  const [ selectedIds    , setSelectedIds    ] = useState<number[]>([]);
  const [ rawPending     , setRawPending     ] = useState<ChatModMessage[]>([]);
  const [ rawRecent      , setRawRecent      ] = useState<ChatModMessage[]>([]);
  const [ overrides      , setOverrides      ] = useState<Map<number, Override>>(new Map());
  const [ error          , setError          ] = useState('');
  const [ lastSync       , setLastSync       ] = useState<number | null>(null);
  const [ bans           , setBans           ] = useState<ChatModBan[]>([]);

  const selectionReady = useRef(false);
  const inFlight       = useRef(false);
  const effectiveId    = demo ? (adminId ?? 1) : adminId;

  // ── Chaînes ───────────────────────────────────────────────────────────────
  const loadChannels = useCallback(async () => {
    if (effectiveId === null) return;
    try {
      const list = await api.channels(effectiveId);
      setChannels(list);
      setChannelsLoaded(true);

      // Première fois : la sélection mémorisée sur ce poste, sinon toutes les
      // chaînes au chat ouvert (à défaut, les chaînes allumées).
      if (!selectionReady.current) {
        selectionReady.current = true;
        const known   = new Set(list.map(c => c.id));
        const stored  = readSelection(effectiveId)?.filter(id => known.has(id));
        const initial = stored && stored.length > 0 ? stored
                      : list.filter(c => c.chatOpen).map(c => c.id);
        setSelectedIds(initial.length > 0 ? initial : list.filter(c => c.active).map(c => c.id));
      }
    } catch (e) {
      setChannelsLoaded(true);
      setError(describeError(e));
    }
  }, [effectiveId]);

  useEffect(() => {
    loadChannels();
    const timer = window.setInterval(loadChannels, CHANNELS_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [loadChannels]);

  // ── Messages ──────────────────────────────────────────────────────────────
  const selectedKey = selectedIds.slice().sort((a, b) => a - b).join(',');

  const loadMessages = useCallback(async () => {
    if (effectiveId === null || inFlight.current) return;
    const ids = selectedKey === '' ? [] : selectedKey.split(',').map(Number);
    if (ids.length === 0) { setRawPending([]); setRawRecent([]); return; }

    inFlight.current = true;
    try {
      const data = await api.messages(effectiveId, ids);
      setRawPending(data.pending);
      setRawRecent(data.recent);
      setLastSync(Date.now());
      setError(prev => (prev.startsWith('Le serveur') || prev.includes('404')) ? '' : prev);

      // Un choix local confirmé par le serveur (ou trop vieux) n'a plus à primer.
      setOverrides(prev => {
        if (prev.size === 0) return prev;
        const byId = new Map<number, ChatMessageStatus>();
        data.pending.forEach(m => byId.set(m.id, m.status));
        data.recent.forEach(m => byId.set(m.id, m.status));
        const next = new Map(prev);
        prev.forEach((o, id) => {
          if (byId.get(id) === o.status || Date.now() - o.at > OVERRIDE_TTL_MS) next.delete(id);
        });
        return next.size === prev.size ? prev : next;
      });
    } catch (e) {
      setError(describeError(e));
    } finally {
      inFlight.current = false;
    }
  }, [effectiveId, selectedKey]);

  useEffect(() => {
    loadMessages();
    let timer: number;
    const schedule = () => {
      timer = window.setTimeout(async () => {
        await loadMessages();
        schedule();
      }, document.hidden ? MESSAGES_HIDDEN_EVERY_MS : MESSAGES_EVERY_MS);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [loadMessages]);

  // ── Vue fusionnée : relevé du serveur + choix locaux en cours ─────────────
  const { pending, recent } = useMemo(() => {
    if (overrides.size === 0) return { pending: rawPending, recent: rawRecent };

    const moved: ChatModMessage[] = [];
    const nextPending = rawPending.filter(m => {
      const o = overrides.get(m.id);
      if (o && o.status !== 'pending') { moved.push({ ...m, status: o.status, moderatedBy: 'admin', moderationDate: new Date(o.at).toISOString() }); return false; }
      return true;
    });
    const nextRecent = rawRecent.map(m => {
      const o = overrides.get(m.id);
      return o && o.status !== m.status ? { ...m, status: o.status, moderatedBy: 'admin' as const } : m;
    });
    return { pending: nextPending, recent: [...moved.reverse(), ...nextRecent] };
  }, [rawPending, rawRecent, overrides]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const toggleChannel = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id];
      if (effectiveId !== null) writeSelection(effectiveId, next);
      return next;
    });
  }, [effectiveId]);

  const selectChannels = useCallback((ids: number[]) => {
    setSelectedIds(ids);
    if (effectiveId !== null) writeSelection(effectiveId, ids);
  }, [effectiveId]);

  const decide = useCallback(async (ids: number[], action: ChatDecision): Promise<boolean> => {
    if (effectiveId === null || ids.length === 0) return false;
    const status: ChatMessageStatus = action === 'approve' ? 'approved' : 'rejected';
    const at = Date.now();
    setOverrides(prev => { const next = new Map(prev); ids.forEach(id => next.set(id, { status, at })); return next; });
    try {
      await api.decide(effectiveId, ids, action);
      return true;
    } catch (e) {
      setOverrides(prev => { const next = new Map(prev); ids.forEach(id => next.delete(id)); return next; });
      setError(describeError(e));
      return false;
    }
  }, [effectiveId]);

  const sanction = useCallback(async (messageId: number, action: ChatSanction) => {
    if (effectiveId === null) return null;
    try {
      const result = await api.sanction(effectiveId, messageId, action);
      loadMessages();
      return result;
    } catch (e) {
      setError(describeError(e));
      return null;
    }
  }, [effectiveId, loadMessages]);

  const updateChannel = useCallback(async (id: number, change: { chatOpen?: boolean; level?: ChatModerationLevel }) => {
    if (effectiveId === null) return false;
    setChannels(prev => prev.map(c => c.id === id ? { ...c, ...change } : c));
    try {
      await api.updateChannel(effectiveId, id, change);
      return true;
    } catch (e) {
      setError(describeError(e));
      loadChannels();
      return false;
    }
  }, [effectiveId, loadChannels]);

  const loadBans = useCallback(async () => {
    if (effectiveId === null) return;
    try { setBans(await api.bans(effectiveId)); } catch (e) { setError(describeError(e)); }
  }, [effectiveId]);

  const liftBan = useCallback(async (banId: number) => {
    if (effectiveId === null) return false;
    try {
      await api.unban(effectiveId, banId);
      setBans(prev => prev.filter(b => b.id !== banId));
      return true;
    } catch (e) {
      setError(describeError(e));
      return false;
    }
  }, [effectiveId]);

  return {
    isDemo: demo, channels, channelsLoaded, selectedIds, pending, recent, error, lastSync, bans,
    toggleChannel, selectChannels, decide, sanction, updateChannel, loadBans, liftBan,
    clearError: () => setError(''),
  };
};
